import { useState, useMemo } from 'react';
import moment from "moment-timezone";
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Combobox } from '@/components/ui/combobox';
import { useToast } from '@/components/ui/use-toast';
import { useListCustomers } from '@/lib/api/customer';
import { useUpsertOrderMetadata, TtOrderMetadata, TtOrderMetadataCustomerSplit } from '@/lib/api/risk/brokerage/tt';
import { SelectOptionItem } from '@/lib/models/ui';
import { Badge } from '@/components/ui/badge';
import { cn, CommonDestinationTimezone } from '@/lib/utils';
import { guessOrderTypeFromText } from '../transform';
import { ContractType, ContractTypeOptions } from '@/lib/api/otc';

type OrderType = 'hta' | 'pricing' | 'position_adjust' | 'cash' | 'otc' | 'none';
type OtcMode = 'hedgeable' | 'reown';

type ValueSource = 'current' | 'suggested' | 'updated';

interface SuggestedValues {
  customerId?: string;
  orderType?: OrderType;
}

interface OrderTextFields {
  textA?: string | null;
  textB?: string | null;
  textC?: string | null;
  textTT?: string | null;
}

interface CustomerOption {
  id: string;
  longName: string;
}

interface CustomerSplitEntry {
  customer_id: string;
  split_percentage: string;
}

interface AttachCustomerFormProps {
  guidOrderId: string;
  existingMetadata?: TtOrderMetadata | null;
  onSuccess?: () => void;
  isInline?: boolean;
  isModal?: boolean;
  orderTextFields?: OrderTextFields;
}

const REOWN_CONTRACT_TYPES = new Set<string>([
  ContractType.BasePriceReown,
  ContractType.RangePriceReown,
  ContractType.ReOwnWDU,
]);

const hedgeableContractTypeOptions = ContractTypeOptions.filter(
  (opt) => !REOWN_CONTRACT_TYPES.has(opt.value)
);

const reownContractTypeOptions = ContractTypeOptions.filter(
  (opt) => REOWN_CONTRACT_TYPES.has(opt.value)
);

/**
 * Suggests a customer ID by matching tokens from text fields against customer long names.
 */
function getSuggestedValues(
  orderTextFields: OrderTextFields | undefined,
  customers: CustomerOption[]
): SuggestedValues {
  const result: SuggestedValues = {};

  if (orderTextFields) {
    const guessed = guessOrderTypeFromText(
      orderTextFields.textA ?? null,
      orderTextFields.textB ?? null,
      orderTextFields.textC ?? null,
      orderTextFields.textTT ?? null
    );
    if (guessed) {
      result.orderType = guessed;
    }
  }

  if (!orderTextFields || customers.length === 0) {
    return result;
  }

  const { textA, textB, textC, textTT } = orderTextFields;

  const combinedText = [textA, textB, textC, textTT]
    .filter((t): t is string => !!t)
    .join(' ');

  if (!combinedText.trim()) {
    return result;
  }

  const tokens = combinedText
    .split(/\s+/)
    .map(t => t.toLowerCase().trim())
    .filter(t => t.length > 0);

  if (tokens.length === 0) {
    return result;
  }

  const scoredCustomers = customers.map(customer => {
    const longNameLower = customer.longName.toLowerCase();
    const matchingTokens = tokens.filter(token => longNameLower.includes(token));
    return {
      customer,
      matchCount: matchingTokens.length,
    };
  });

  const customersWithMatches = scoredCustomers.filter(sc => sc.matchCount > 0);

  if (customersWithMatches.length === 0) {
    return result;
  }

  customersWithMatches.sort((a, b) => b.matchCount - a.matchCount);
  result.customerId = customersWithMatches[0].customer.id;
  return result;
}

function ValueSourceBadge({ source }: { source: ValueSource }) {
  const config = {
    current: { label: 'Current', className: 'bg-muted text-muted-foreground' },
    suggested: { label: 'Suggested', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' },
    updated: { label: 'Updated', className: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' },
  };
  const { label, className } = config[source];
  return <Badge variant="outline" className={cn('ml-2 text-xs font-normal', className)}>{label}</Badge>;
}

function getOrderTypeLabel(orderType: OrderType): string {
  const labels: Record<OrderType, string> = {
    none: 'None',
    hta: 'HTA',
    pricing: 'Pricing',
    position_adjust: 'Position Adjust',
    cash: 'Cash',
    otc: 'OTC',
  };
  return labels[orderType];
}

export function AttachCustomerForm({
  guidOrderId,
  existingMetadata,
  onSuccess,
  isInline = false,
  isModal = false,
  orderTextFields,
}: AttachCustomerFormProps) {
  const { toast } = useToast();
  const customersResult = useListCustomers({ minimal: true, hasRiskProfile: true });
  const upsertMetadata = useUpsertOrderMetadata();

  const customerOptionsForMatching: CustomerOption[] = useMemo(() => {
    const customers = customersResult.data?.data ?? [];
    return customers.map((customer: { id: string; short_name?: string; long_name?: string }) => ({
      id: customer.id,
      longName: customer.long_name || customer.short_name || customer.id,
    }));
  }, [customersResult.data?.data]);

  const suggestedValues = useMemo(
    () => getSuggestedValues(orderTextFields, customerOptionsForMatching),
    [orderTextFields, customerOptionsForMatching]
  );

  const getExistingOrderType = (): OrderType => {
    if (!existingMetadata) return 'none';
    if (existingMetadata.expected_hta) return 'hta';
    if (existingMetadata.expected_pricing) return 'pricing';
    if (existingMetadata.expected_position_adjust) return 'position_adjust';
    if (existingMetadata.expected_cash) return 'cash';
    if (existingMetadata.expected_otc) return 'otc';
    return 'none';
  };

  const existingOrderType = getExistingOrderType();

  const getInitialCustomerSplits = (): CustomerSplitEntry[] => {
    if (existingMetadata?.SpecifiedCustomers && existingMetadata.SpecifiedCustomers.length > 0) {
      return existingMetadata.SpecifiedCustomers.map((c: TtOrderMetadataCustomerSplit) => ({
        customer_id: c.customer_id,
        split_percentage: String(c.split_percentage),
      }));
    }
    if (suggestedValues.customerId) {
      return [{ customer_id: suggestedValues.customerId, split_percentage: '100' }];
    }
    return [{ customer_id: '', split_percentage: '100' }];
  };

  const getInitialOrderType = (): OrderType => {
    if (existingOrderType !== 'none') return existingOrderType;
    if (suggestedValues.orderType) return suggestedValues.orderType;
    return 'none';
  };

  const getInitialOtcMode = (): OtcMode | null => {
    const existing = existingMetadata?.otc_contract_type;
    if (!existing) return null;
    return REOWN_CONTRACT_TYPES.has(existing) ? 'reown' : 'hedgeable';
  };

  const currentYear = moment.tz(CommonDestinationTimezone).format("YYYY");

  const [customerSplits, setCustomerSplits] = useState<CustomerSplitEntry[]>(getInitialCustomerSplits());
  const [selectedCropYear, setSelectedCropYear] = useState<string>(
    existingMetadata?.crop_year !== 0 ? existingMetadata?.crop_year?.toString() ?? currentYear : currentYear
  );
  const [orderType, setOrderType] = useState<OrderType>(getInitialOrderType());
  const [shouldIgnore, setShouldIgnore] = useState<boolean>(existingMetadata?.should_ignore_order ?? false);
  const [otcMode, setOtcMode] = useState<OtcMode | null>(getInitialOtcMode());
  const [otcContractType, setOtcContractType] = useState<string>(existingMetadata?.otc_contract_type ?? '');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const customerOptions: SelectOptionItem<string>[] = useMemo(() => {
    const customers = customersResult.data?.data ?? [];
    return customers.map((customer: { id: string; short_name?: string; long_name?: string }) => ({
      value: customer.id,
      label: customer.long_name || customer.short_name || customer.id,
    }));
  }, [customersResult.data?.data]);

  const getCustomerName = (customerId: string): string => {
    const option = customerOptions.find(opt => opt.value === customerId);
    return option?.label ?? customerId;
  };

  const getCustomerValueSource = (): ValueSource => {
    const firstId = customerSplits[0]?.customer_id ?? '';
    const existingFirstId = existingMetadata?.SpecifiedCustomers?.[0]?.customer_id ?? '';
    if (existingFirstId && firstId !== existingFirstId) return 'updated';
    if (existingFirstId && firstId === existingFirstId) return 'current';
    if (!existingFirstId && suggestedValues.customerId && firstId === suggestedValues.customerId) return 'suggested';
    if (!existingFirstId && firstId) return 'updated';
    return 'current';
  };

  const getOrderTypeValueSource = (): ValueSource => {
    if (existingOrderType !== 'none') {
      if (orderType !== existingOrderType) return 'updated';
      return 'current';
    }
    if (suggestedValues.orderType && orderType === suggestedValues.orderType) return 'suggested';
    if (orderType !== 'none') return 'updated';
    return 'current';
  };

  const handleOtcModeSelect = (mode: OtcMode) => {
    setOtcMode(mode);
    setOtcContractType('');
  };

  const handleAddCustomerSplit = () => {
    setCustomerSplits(prev => [...prev, { customer_id: '', split_percentage: '0' }]);
  };

  const handleRemoveCustomerSplit = (index: number) => {
    setCustomerSplits(prev => prev.filter((_, i) => i !== index));
  };

  const handleCustomerSplitChange = (index: number, field: keyof CustomerSplitEntry, value: string) => {
    setCustomerSplits(prev =>
      prev.map((entry, i) => i === index ? { ...entry, [field]: value } : entry)
    );
  };

  const handleOrderTypeChange = (value: string) => {
    setOrderType(value as OrderType);
    if (value !== 'otc') {
      setOtcMode(null);
      setOtcContractType('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    const firstCustomerId = customerSplits[0]?.customer_id ?? '';
    if (!firstCustomerId) {
      toast({
        title: 'Validation Error',
        description: 'Please select at least one customer',
        variant: 'destructive',
      });
      return;
    }

    if (!selectedCropYear) {
      toast({
        title: 'Validation Error',
        description: 'Please select a crop year',
        variant: 'destructive',
      });
      return;
    }

    if (orderType === 'otc' && !otcContractType) {
      toast({
        title: 'Validation Error',
        description: 'Please select an OTC contract type',
        variant: 'destructive',
      });
      return;
    }

    const validSplits = customerSplits.filter(s => s.customer_id);
    if (validSplits.length === 0) {
      toast({
        title: 'Validation Error',
        description: 'Please select at least one customer',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await upsertMetadata.mutateAsync({
        guid_order_id: guidOrderId,
        expected_hta: orderType === 'hta',
        expected_pricing: orderType === 'pricing',
        expected_position_adjust: orderType === 'position_adjust',
        expected_cash: orderType === 'cash',
        expected_otc: orderType === 'otc',
        otc_contract_type: orderType === 'otc' ? otcContractType : undefined,
        should_ignore_order: shouldIgnore,
        crop_year: parseInt(selectedCropYear),
        customers: validSplits.map(s => ({
          customer_id: s.customer_id,
          split_percentage: s.split_percentage,
        })),
      });

      if (result.success) {
        toast({
          title: 'Success',
          description: 'Order metadata updated successfully',
        });
        onSuccess?.();
      } else {
        throw new Error(result.message || 'Failed to update metadata');
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update metadata';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasExistingMetadata = !!existingMetadata;

  const hasTextFields = orderTextFields && (
    orderTextFields.textA || orderTextFields.textB || orderTextFields.textC || orderTextFields.textTT
  );

  const availableOtcContractTypes = otcMode === 'reown'
    ? reownContractTypeOptions
    : otcMode === 'hedgeable'
      ? hedgeableContractTypeOptions
      : [];

  const cropYearOptions: SelectOptionItem<string>[] = Array.from({ length: 10 }, (_, i) => {
    const year = parseInt(currentYear) - i + 3;
    return {
      value: year.toString(),
      label: year.toString(),
    };
  });

  return (
    <form onSubmit={handleSubmit} className={isInline ? 'space-y-4' : 'space-y-4 py-4'}>
      {/* Order Text Fields Summary - only shown in modal */}
      {isModal && hasTextFields && (
        <div className="p-3 bg-muted/50 rounded-lg border text-sm">
          <div className="grid grid-cols-4 gap-x-4 gap-y-1">
            {orderTextFields.textA && (
              <span className="grid">
                <span className="text-muted-foreground">Text A:</span>
                <span className="font-medium">{orderTextFields.textA}</span>
              </span>
            )}
            {orderTextFields.textB && (
              <span className="grid">
                <span className="text-muted-foreground">Text B:</span>
                <span className="font-medium">{orderTextFields.textB}</span>
              </span>
            )}
            {orderTextFields.textC && (
              <span className="grid">
                <span className="text-muted-foreground">Text C:</span>
                <span className="font-medium">{orderTextFields.textC}</span>
              </span>
            )}
            {orderTextFields.textTT && (
              <span className="grid">
                <span className="text-muted-foreground">Text TT:</span>
                <span className="font-medium">{orderTextFields.textTT}</span>
              </span>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left Column: Existing State (Read-only) */}
        <div className="space-y-4 p-4 bg-muted/30 rounded-lg border">
          <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
            Existing Metadata
          </h3>
          {hasExistingMetadata ? (
            <div className="space-y-4">
              <div className="space-y-1">
                <Label className="text-muted-foreground text-xs">Customer(s)</Label>
                {existingMetadata.SpecifiedCustomers && existingMetadata.SpecifiedCustomers.length > 0 ? (
                  <div className="space-y-1">
                    {existingMetadata.SpecifiedCustomers.map((c: TtOrderMetadataCustomerSplit, i: number) => (
                      <p key={i} className="text-sm font-medium">
                        {c.Customer?.long_name ?? getCustomerName(c.customer_id)}
                        {existingMetadata.SpecifiedCustomers.length > 1 && (
                          <span className="text-muted-foreground ml-1 text-xs">({c.split_percentage}%)</span>
                        )}
                      </p>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm font-medium">
                    <span className="text-muted-foreground italic">Not set</span>
                  </p>
                )}
              </div>
              <div className="space-y-1">
                <Label className="text-muted-foreground text-xs">Order Type</Label>
                <p className="text-sm font-medium">{getOrderTypeLabel(existingOrderType)}</p>
              </div>
              {existingMetadata.expected_otc && existingMetadata.otc_contract_type && (
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">OTC Contract Type</Label>
                  <p className="text-sm font-medium">
                    {ContractTypeOptions.find(o => o.value === existingMetadata.otc_contract_type)?.label ?? existingMetadata.otc_contract_type}
                  </p>
                </div>
              )}
              <div className="space-y-1">
                <Label className="text-muted-foreground text-xs">Crop Year</Label>
                <p className="text-sm font-medium">{existingMetadata?.crop_year ?? ""}</p>
              </div>
              <div className="space-y-1">
                <Label className="text-muted-foreground text-xs">Should Ignore</Label>
                <p className="text-sm font-medium">{existingMetadata.should_ignore_order ? 'Yes' : 'No'}</p>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-24 text-muted-foreground italic text-sm">
              No existing metadata
            </div>
          )}
        </div>

        {/* Right Column: Editable Form */}
        <div className="space-y-4">
          <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
            {hasExistingMetadata ? 'Update Metadata' : 'Set Metadata'}
          </h3>

          {/* Customer Splits */}
          <div className="space-y-2">
            <div className="flex items-center">
              <Label>Customer(s) <span className="text-destructive">*</span></Label>
              {customerSplits[0]?.customer_id && <ValueSourceBadge source={getCustomerValueSource()} />}
            </div>
            <div className="space-y-2">
              {customerSplits.map((entry, index) => (
                <div key={index} className="flex gap-2 items-start">
                  <div className="flex-1">
                    <Combobox
                      dwimOnChange
                      isModal={isModal}
                      placeholder="Select customer..."
                      options={customerOptions}
                      value={entry.customer_id}
                      onValueChange={(val) => handleCustomerSplitChange(index, 'customer_id', val)}
                      disabled={customersResult.isLoading}
                      virtualizeList
                      popoverClassName="w-[400px] h-[200px]"
                    />
                  </div>
                  {customerSplits.length > 1 && (
                    <div className="w-20">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="1"
                        value={entry.split_percentage}
                        onChange={(e) => handleCustomerSplitChange(index, 'split_percentage', e.target.value)}
                        className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm"
                        placeholder="%"
                      />
                    </div>
                  )}
                  {customerSplits.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveCustomerSplit(index)}
                      className="h-9 px-2 text-destructive hover:text-destructive"
                    >
                      ✕
                    </Button>
                  )}
                </div>
              ))}
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAddCustomerSplit}
              className="mt-1"
            >
              + Add Split
            </Button>
          </div>

          {/* Crop Year */}
          <div className="space-y-2 grid">
            <div className="flex items-center">
              <Label>Crop Year <span className="text-destructive">*</span></Label>
            </div>
            <Combobox
              dwimOnChange
              isModal={isModal}
              placeholder="Select crop year..."
              options={cropYearOptions}
              value={selectedCropYear}
              onValueChange={setSelectedCropYear}
            />
          </div>

          {/* Order Type */}
          <div className="space-y-2">
            <div className="flex items-center">
              <Label>Order Type</Label>
              {(orderType !== 'none' || existingOrderType !== 'none') && (
                <ValueSourceBadge source={getOrderTypeValueSource()} />
              )}
            </div>
            <RadioGroup
              value={orderType}
              onValueChange={handleOrderTypeChange}
              className="flex flex-col space-y-1"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="none" id="type-none" />
                <Label htmlFor="type-none" className="font-normal cursor-pointer">None</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="hta" id="type-hta" />
                <Label htmlFor="type-hta" className="font-normal cursor-pointer">HTA</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="pricing" id="type-pricing" />
                <Label htmlFor="type-pricing" className="font-normal cursor-pointer">Pricing</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="position_adjust" id="type-position-adjust" />
                <Label htmlFor="type-position-adjust" className="font-normal cursor-pointer">Position Adjust</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="cash" id="type-cash" />
                <Label htmlFor="type-cash" className="font-normal cursor-pointer">Cash</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="otc" id="type-otc" />
                <Label htmlFor="type-otc" className="font-normal cursor-pointer">OTC</Label>
              </div>
            </RadioGroup>
          </div>

          {/* OTC Sub-section */}
          {orderType === 'otc' && (
            <div className="space-y-3 pl-4 border-l-2 border-muted">
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={otcMode === 'hedgeable' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleOtcModeSelect('hedgeable')}
                >
                  Hedgeable
                </Button>
                <Button
                  type="button"
                  variant={otcMode === 'reown' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleOtcModeSelect('reown')}
                >
                  Reown
                </Button>
              </div>

              {otcMode !== null && (
                <div className="space-y-1 grid">
                  <Label>
                    OTC Contract Type <span className="text-destructive">*</span>
                  </Label>
                  <Combobox
                    dwimOnChange
                    isModal={isModal}
                    placeholder="Select contract type..."
                    options={availableOtcContractTypes}
                    value={otcContractType}
                    onValueChange={setOtcContractType}
                    popoverClassName="w-[300px]"
                  />
                </div>
              )}
            </div>
          )}

          {/* Should Ignore */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="should-ignore"
              checked={shouldIgnore}
              onCheckedChange={(checked) => setShouldIgnore(checked === true)}
            />
            <Label htmlFor="should-ignore" className="font-normal cursor-pointer">
              Should Ignore Order
            </Label>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </form>
  );
}
